import React, {
  CSSProperties,
  ChangeEvent,
  KeyboardEvent,
  PropsWithChildren,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { Icon, InputContainer, InputElement, InputWrapper, Portal, PortalPlacement } from '@column/column-ui-kit';

import { checkDateParts, getDateFormatFromValues, getDateParts } from '~/util';

import { PopoverDatepicker } from './Popover';

export interface DateInputStyleProps {
  placement?: PortalPlacement;
  isFocussed: boolean;
  isHovered: boolean;
  isDisabled: boolean;
  hasError: boolean;
  size: 'default' | 'small';
  variant: 'default' | 'light';
}

export interface DateInputProps extends Partial<DateInputStyleProps> {
  onChange?: (value: string | undefined) => void;
  onClick?: () => void;
  onHoverChange?: (value: boolean) => void;
  className?: string;
  value?: string | undefined;
  style?: CSSProperties;
  disableBefore?: Date;
  disableAfter?: Date;
  renderInside?: boolean;
}

const DateInputWrapper = styled.div`
  position: relative;
`;

const Fields = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const Divider = styled.div`
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(400)};
`;

const DayField = styled(InputElement)`
  width: 24px;
`;

const MonthField = styled(InputElement)`
  width: 30px;
`;

const YearField = styled(InputElement)`
  width: 44px;
`;

const Calendar = styled(Icon.Calendar)<DateInputStyleProps>`
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  --icon-size: 16px;

  path {
    transition:
      stroke 0.2s,
      fill 0.2s;
  }

  &:hover {
    --icon-color: ${({ theme }) => theme.secondary.background};
  }

  ${({ isFocussed }) =>
    isFocussed &&
    css`
      --icon-color: ${({ theme }) => theme.primary.background};
    `}
`;

const CalendarWrapper = styled.div`
  margin-left: auto;
  cursor: pointer;
`;

const StyledPopoverDatepicker = styled(PopoverDatepicker)<{ $renderInside: boolean }>`
  --popover-width: 280px;
  --popover-padding: 16px;
  --popover-top: auto;
  --popover-left: auto;
  --popover-right: auto;
  --popover-position: relative;
  --popover-z-index: 16;

  z-index: 20;

  ${({ $renderInside }) =>
    $renderInside &&
    css`
      --popover-top: 32px;
      --popover-right: -28px;
      --popover-position: absolute;
    `}
`;

export const DateInput = forwardRef<HTMLLabelElement, PropsWithChildren<DateInputProps>>((props, ref) => {
  const [isHoveredState, setIsHoveredState] = useState<boolean>(props.isHovered ?? false);
  const [hasErrorState, setHasErrorState] = useState<boolean>(props.hasError ?? false);
  const [isMonthFocussedState, setIsMonthFocussedState] = useState<boolean>(false);
  const [isDayFocussedState, setIsDayFocussedState] = useState<boolean>(false);
  const [isYearFocussedState, setIsYearFocussedState] = useState<boolean>(false);
  const [monthValue, setMonthValue] = useState<string>('');
  const [dayValue, setDayValue] = useState<string>('');
  const [yearValue, setYearValue] = useState<string>('');
  const [showDatepicker, setShowDatepicker] = useState<boolean>(false);

  const calendarRef = useRef<HTMLDivElement>(null);

  const monthInputRef = useRef<HTMLInputElement>(null);
  const dayInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);

  const styleProps: DateInputStyleProps = {
    isHovered: props.isHovered ?? isHoveredState,
    isFocussed: (isMonthFocussedState || isDayFocussedState || isYearFocussedState) ?? false,
    isDisabled: props.isDisabled ?? false,
    hasError: props.hasError ?? hasErrorState,
    size: props.size ?? 'default',
    variant: props.variant ?? 'default',
  };

  const handleClick = () => {
    if (props.isDisabled) {
      return;
    }
    if (props.onClick) {
      props.onClick();
    }
    setIsMonthFocussedState(true);
  };

  const handleCalendarClick = (event: React.MouseEvent) => {
    if (props.isDisabled) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    setShowDatepicker(true);
  };

  const setNewDateValues = (value: string | undefined) => {
    if (!value) {
      setYearValue('');
      setMonthValue('');
      setDayValue('');
      return;
    }

    if (!checkDateParts(value)) {
      return;
    }

    const { year, month, day } = getDateParts(value);

    setYearValue(year);
    setMonthValue(month);
    setDayValue(day);
  };

  const handleDatePicker = (value: string | string[] | undefined) => {
    if (Array.isArray(value) || !value) {
      return;
    }

    setNewDateValues(value);

    setShowDatepicker(false);
  };

  const handleCalendarClose = () => {
    setShowDatepicker(false);
  };

  const handleHoverChange = (value: boolean) => {
    if (props.isDisabled) {
      return;
    }
    if (props.onHoverChange) {
      props.onHoverChange(value);
    }
    setIsHoveredState(value);
  };

  const handleMonthChange = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    setMonthValue(target.value);
  };

  const handleMonthKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const length = target.value.length;

    if (length === 2 && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Backspace') {
      setIsMonthFocussedState(false);
      setIsDayFocussedState(true);
    }
    if (target.selectionStart === length && event.key === 'ArrowRight') {
      setIsMonthFocussedState(false);
      setIsDayFocussedState(true);
    }
  };

  const handleDayChange = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    setDayValue(target.value);
  };

  const handleDayKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const length = target.value.length;

    if (length === 2 && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Backspace') {
      setIsDayFocussedState(false);
      setIsYearFocussedState(true);
    }
    if (target.selectionStart === 0 && (event.key === 'ArrowLeft' || event.key === 'Backspace')) {
      setIsDayFocussedState(false);
      setIsMonthFocussedState(true);
    }
    if (target.selectionStart === length && event.key === 'ArrowRight') {
      setIsDayFocussedState(false);
      setIsYearFocussedState(true);
    }
  };

  const handleYearChange = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    setYearValue(target.value);
  };

  const handleYearKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const length = target.value.length;
    if (target.selectionStart === 0 && (event.key === 'ArrowLeft' || event.key === 'Backspace')) {
      setIsYearFocussedState(false);
      setIsDayFocussedState(true);
    }
    if (target.selectionStart === length && event.key === 'ArrowRight') {
      setIsYearFocussedState(false);
      setIsMonthFocussedState(true);
    }
  };

  useEffect(() => {
    if (isMonthFocussedState) {
      monthInputRef.current?.focus();
    } else {
      monthInputRef.current?.blur();
    }
  }, [isMonthFocussedState, monthInputRef]);

  useEffect(() => {
    if (isDayFocussedState) {
      dayInputRef.current?.focus();
    } else {
      dayInputRef.current?.blur();
    }
  }, [isDayFocussedState, dayInputRef]);

  useEffect(() => {
    if (isYearFocussedState) {
      yearInputRef.current?.focus();
    } else {
      yearInputRef.current?.blur();
    }
  }, [isYearFocussedState, yearInputRef]);

  useEffect(() => {
    const dateFormat = getDateFormatFromValues(yearValue, monthValue, dayValue);

    const validDate = checkDateParts(dateFormat);

    setHasErrorState(
      !!(
        (!validDate && !!monthValue && !!dayValue && !!yearValue) ||
        (props.disableBefore && validDate && props.disableBefore > new Date(dateFormat)) ||
        (props.disableAfter && validDate && props.disableAfter < new Date(dateFormat))
      )
    );

    if (!validDate) {
      return;
    }

    if (props.onChange && dateFormat !== props.value) {
      props.onChange(dateFormat);
    }
  }, [monthValue, dayValue, yearValue]);

  useEffect(() => {
    if (props.value !== undefined) {
      setNewDateValues(props.value);
    }
  }, [props.value]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  return (
    <DateInputWrapper ref={wrapperRef}>
      <InputWrapper
        ref={ref}
        style={props.style}
        className={props.className}
        onMouseEnter={() => handleHoverChange(true)}
        onMouseMove={() => handleHoverChange(true)}
        onMouseLeave={() => handleHoverChange(false)}
        onClick={handleClick}
        {...styleProps}
      >
        <InputContainer {...styleProps}>
          <Fields>
            <MonthField
              onChange={handleMonthChange}
              onFocus={() => setIsMonthFocussedState(true)}
              onBlur={() => setIsMonthFocussedState(false)}
              onKeyUp={handleMonthKeyUp}
              ref={monthInputRef}
              disabled={props.isDisabled ?? false}
              value={monthValue}
              type="tel"
              pattern="^[0-9]*$"
              maxLength={2}
              placeholder="mm"
            />
            <Divider>/</Divider>
            <DayField
              onChange={handleDayChange}
              onFocus={() => setIsDayFocussedState(true)}
              onBlur={() => setIsDayFocussedState(false)}
              onKeyUp={handleDayKeyUp}
              ref={dayInputRef}
              disabled={props.isDisabled ?? false}
              value={dayValue}
              type="tel"
              pattern="^[0-9]*$"
              maxLength={2}
              placeholder="dd"
            />
            <Divider>/</Divider>
            <YearField
              onChange={handleYearChange}
              onFocus={() => setIsYearFocussedState(true)}
              onBlur={() => setIsYearFocussedState(false)}
              onKeyUp={handleYearKeyUp}
              ref={yearInputRef}
              disabled={props.isDisabled ?? false}
              value={yearValue}
              type="tel"
              pattern="^[0-9]*$"
              maxLength={4}
              placeholder="yyyy"
            />
          </Fields>
          <CalendarWrapper ref={calendarRef}>
            <Calendar onClick={handleCalendarClick} {...styleProps} />
          </CalendarWrapper>
        </InputContainer>
        {props.children}
      </InputWrapper>
      <Portal
        reference={calendarRef}
        placement={props.placement || (props.renderInside ? 'top-start' : 'bottom-end')}
        offsetMain={props.renderInside ? undefined : 20}
        offsetCross={props.renderInside ? undefined : props.size === 'small' ? 12 : 16}
        zIndex={16}
        shouldCalculate={{ is: showDatepicker, timeout: 200 }}
        renderInside={props.renderInside}
      >
        <StyledPopoverDatepicker
          show={showDatepicker}
          date={props.value}
          onDateSubmit={handleDatePicker}
          onClose={handleCalendarClose}
          disableBefore={props.disableBefore}
          disableAfter={props.disableAfter}
          $renderInside={props.renderInside ?? false}
        />
      </Portal>
    </DateInputWrapper>
  );
});
